import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { capitalize, find, isEqual, map, omit, pick } from 'lodash';
import { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import { format, subYears } from 'date-fns';
import PropTypes from 'prop-types';
import { utcToZonedTime } from 'date-fns-tz';

import gender from 'common/constants/gender';
import DateField from 'common/forms/DateField';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import languages from 'common/constants/languages';
import {
  validateEmail,
  validateMaxDate,
  validatePhoneAllowedInState,
  validatePhoneNumber,
  validateValueWithRegex,
} from 'common/forms/formValidations';
import { editPatient } from 'store/thunks/patientThunks';
import CheckboxField from 'common/forms/CheckboxField';
import { ShowDefaultPartnerContact } from 'common/utils/featureFlags';

import DefaultContactChip from './DefaultContactChip';

const EditPatientButton = ({ mpi }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const patientMinAge = Number(process.env.REACT_APP_PATIENT_MIN_AGE) || 10;

  const patientInfo = useSelector(({ patient }) => {
    return {
      ...pick(patient[mpi], [
        'firstName',
        'lastName',
        'middleName',
        'gender',
        'email',
        'mobilePhone',
        'last4ssn',
        'notificationPreferences',
      ]),
      dob: patient[mpi]?.dob,
      primaryLanguage: find(patient[mpi]?.languages, 'isPrimary')?.language,
      address: find(patient[mpi]?.addresses, 'isDefault')?.address,
    };
  }, isEqual);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const handleEditPatient = (formData) => {
    const patientData = {
      ...omit(formData, [
        'primaryLanguage',
        'notificationPreferences',
        'emailPreference',
        'phonePreference',
        'address',
      ]),
      languages: [{ language: formData.primaryLanguage, isPrimary: true }],
      dob: format(new Date(formData.dob), 'yyyy-MM-dd'),
      last4ssn: formData.last4ssn || '',
      middleName: formData.middleName || '',
      // Home and work phone are left out: an omitted field keeps what is on file.
      mpi,
      preferencesState: {
        email: formData.emailPreference,
        phone: formData.phonePreference,
      },
    };

    return dispatch(editPatient({ patientData })).then((success) => {
      if (success) {
        handleCloseModal();
      }
    });
  };

  const initialFormValues = useMemo(
    () => ({
      ...patientInfo,
      dob: patientInfo?.dob && utcToZonedTime(new Date(patientInfo.dob), 'utc'),
      emailPreference: patientInfo?.notificationPreferences?.email,
      phonePreference: patientInfo?.notificationPreferences?.phone,
    }),
    [patientInfo]
  );

  return (
    <>
      <Button
        color='secondary'
        variant='outlined'
        onClick={handleOpenModal}
        size='small'
        sx={{ height: theme.spacing(4) }}
        disabled={!patientInfo.firstName}
      >
        Edit Patient
      </Button>

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='PatientView-EditPatientButton-header'
        fullWidth
      >
        <DialogTitle id='PatientView-EditPatientButton-header'>Edit Patient</DialogTitle>

        <Form
          initialValues={initialFormValues}
          onSubmit={handleEditPatient}
          render={({ handleSubmit, invalid, submitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <TextField
                  id='PatientView-EditPatientButton-firstName-input'
                  label='First Name'
                  name='firstName'
                  required
                  autoFocus
                />

                <TextField
                  id='PatientView-EditPatientButton-lastName-input'
                  label='Last Name'
                  name='lastName'
                  required
                />

                <TextField
                  id='PatientView-EditPatientButton-middleName-input'
                  label='Middle Name'
                  name='middleName'
                />

                <DateField
                  name='dob'
                  id='PatientView-EditPatientButton-dob-input'
                  label='Date of Birth'
                  maxDate={subYears(new Date(), patientMinAge)}
                  required
                  validations={[
                    validateMaxDate(
                      `Patient must be at least ${patientMinAge} years old`,
                      subYears(new Date(), patientMinAge)
                    ),
                  ]}
                />

                <TextField
                  id='PatientView-EditPatientButton-ssn-input'
                  label='SSN (Last 4)'
                  name='last4ssn'
                  validations={[validateValueWithRegex('Must be 4 numbers', /^\d{4}$/)]}
                />

                <SelectField
                  name='gender'
                  id='PatientView-EditPatientButton-gender-dropdown'
                  label='Gender'
                  required
                >
                  {map(gender, (name, value) => (
                    <MenuItem value={value} key={value}>
                      {name}
                    </MenuItem>
                  ))}
                </SelectField>

                <SelectField
                  name='primaryLanguage'
                  id='PatientView-EditPatientButton-primaryLanguage-dropdown'
                  label='Primary Language Spoken'
                  required
                >
                  {map(languages, (language) => (
                    <MenuItem key={language} value={language}>
                      {capitalize(language)}
                    </MenuItem>
                  ))}
                </SelectField>

                {/* Switch off: the default email and phone are not shown; the form still sends the
                    values on file back unchanged, so saving never alters them. */}
                {ShowDefaultPartnerContact && (
                  <>
                    <TextField
                      id='PatientView-EditPatientButton-email-input'
                      label='Email'
                      labelAdornment={<DefaultContactChip contactKind='email' />}
                      name='email'
                      required
                      validations={[validateEmail()]}
                    />

                    <TextField
                      id='PatientView-EditPatientButton-mobilePhoneNumber-input'
                      label='Mobile Phone Number'
                      labelAdornment={<DefaultContactChip contactKind='phone' />}
                      name='mobilePhone'
                      required
                      validations={[validatePhoneNumber()]}
                    />
                  </>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: 2,
                    alignItems: 'start',
                  }}
                >
                  <CheckboxField
                    label='Email Notification Preference'
                    name='emailPreference'
                    id='PatientView-EditPatientButton-emailPreference-checkbox'
                  />

                  <CheckboxField
                    label='Phone Notification Preference'
                    name='phonePreference'
                    id='PatientView-EditPatientButton-phonePreference-checkbox'
                    validations={[validatePhoneAllowedInState(undefined, 'address.state')]}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={submitting}
                  variant='contained'
                  disabled={invalid}
                  type='submit'
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

EditPatientButton.propTypes = {
  mpi: PropTypes.string.isRequired,
};

export default EditPatientButton;
